/**
 * OMS Orders list — column visibility, labels, and field mapping rules.
 * Edit this file to change show/hide, labels, or field paths without touching render logic.
 *
 * Platform-scoped values (sync_status, allocation_status, api_code) are resolved in
 * OmsOrders.tsx via platformorder_set.find(item => item.platform === platformId).
 */

/** Dynamic status columns from metadata that are fully hidden in OMS view. */
export const hiddenDynamicStatusChannelTypes = ['invoice', 'shipping_label'] as const

/**
 * Metadata channel_type values replaced by a fixed OMS column (same position in metadata order).
 * Field paths are resolved in OmsOrders.tsx (top-level row vs platformorder_set by platform id).
 */
export const replacedDynamicStatusColumns = {
  shipment: {
    label: 'Fulfillment Status',
    /** Top-level row field */
    rowField: 'shipment_status',
    sortField: 'shipment_status',
    filterKey: 'shipment_status',
    filterValues: [
      {value: 'SHIPPED', label: 'Shipped'},
      {value: 'PARTIALLY_SHIPPED', label: 'Partially Shipped'},
      {value: 'PENDING', label: 'Pending'},
      {value: 'PROCESSING', label: 'Processing'},
      {value: 'ERROR', label: 'Error'},
    ],
    tooltip:
      'Indicates if the shipping information and tracking numbers have been successfully synchronized between platforms.',
  },
  oms: {
    label: 'Allocation Status',
    /** platformorder_set[].allocation_status when item.platform === OMS platform id */
    platformOrderField: 'allocation_status',
    filterKey: 'allocation_status',
    filterValues: [
      {value: 'ALLOCATED', label: 'Allocated'},
      {value: 'UNALLOCATED', label: 'Unallocated'},
      {value: 'PARTIALLY_ALLOCATED', label: 'Partially Allocated'},
    ],
    tooltip: 'Shows whether order line items are fully, partially, or not allocated in Commercium OMS.',
  },
} as const

/** Sync status columns kept from metadata (platformorder_set[].sync_status by platform id). */
export const dynamicSyncStatusChannelTypes = [
  'primary',
  'accounting',
  'shipping',
  'erp',
] as const

/** Order # columns: same as Sales Order Logs — platformorder_set[].api_code by platform id. */
export const dynamicOrderNumberHiddenChannelTypes = [
  'invoice',
  'shipment',
  'shipping_label',
  'oms',
] as const

/** Display order for platform sync status columns (after fixed OMS status cols). */
export const dynamicSyncStatusChannelTypeOrder = [
  'shipping',
  'erp',
  'accounting',
  'primary',
] as const

/** OMS order log only — sync status columns (Shipping + Accounting stores). */
export const omsOrderLogSyncStatusChannelTypes = ['shipping', 'accounting'] as const

export const omsOrderLogSyncStatusChannelTypeOrder = ['shipping', 'accounting'] as const

/** Display order for channel order # columns (before Action). */
export const dynamicOrderNumberChannelTypeOrder = [
  'shipping',
  'erp',
  'accounting',
  'primary',
] as const

/** Override metadata display_name for dynamic sync status columns (by channel_type). */
export const dynamicStatusColumnLabelOverrides: Partial<Record<string, string>> = {
  shipping: 'Shipping Store Status',
}

/** Source Cancel Status is hidden; replaced by Order Status. */
export const showCancelColumn = false

export const orderStatusColumn = {
  label: 'Order Status',
  displayPath: 'api_status',
  sortField: 'api_status',
  searchFilterKey: 'api_status',
  tooltip: 'The current lifecycle stage of the order within Commercium OMS.',
} as const

/**
 * Master column layout — single source of truth
 *
 * - To reorder columns: reorder this array.
 * - To hide a column: set `visible: false` (or remove the entry).
 * - Status filter values/keys live in `replacedDynamicStatusColumns` / `orderStatusColumn`.
 * - Per-platform groups (sync status, channel order #) are configured via the `dynamic*` arrays above.
 */
export type OmsColumnRenderer =
  | 'channelOrderName'
  | 'date'
  | 'allocationStatus'
  | 'fulfillmentStatus'
  | 'orderStatus'
  | 'syncStatusGroup'
  | 'channel'
  | 'channelOrderNoGroup'
  | 'action'

export type OmsColumnDescriptor = {
  /** Stable identifier for the column. */
  id: string
  /** Renderer key resolved to a cell/header builder in OmsOrders.tsx. */
  renderer: OmsColumnRenderer
  /** Header label (static columns). */
  header?: string
  /** Header info-icon tooltip text. */
  tooltip?: string
  /** Dot path on the row for the displayed value (e.g. 'api_created_at'). */
  valuePath?: string
  /** Value shown when the resolved path is empty. */
  fallback?: string
  /** Server sort field. */
  sortField?: string
  /** Fixed column width. */
  width?: string
  /** Minimum column width. */
  minWidth?: string
  /** Set false to hide without deleting. */
  visible?: boolean
}

export const columnLayout: OmsColumnDescriptor[] = [
  {
    id: 'channelOrderNo',
    renderer: 'channelOrderName',
    header: 'Order #',
    tooltip:
      'The unique identification number assigned to the order by the original sales marketplace/Platform',
    sortField: 'api_code',
    minWidth: '150px',
  },
  {
    id: 'orderCreateDate',
    renderer: 'date',
    header: 'Order Create Date',
    tooltip:
      'The exact date and time the customer placed the order on the external sales channel. Showing in your Timezone.',
    valuePath: 'api_created_at',
    fallback: 'N/A',
    sortField: 'api_created_at',
    width: '160px',
  },
  {
    id: 'importDate',
    renderer: 'date',
    header: 'Import Date',
    tooltip:
      'The timestamp indicating when Commercium successfully fetched and recorded the order data from the connected channel. Showing in your Timezone.',
    valuePath: 'created_at',
    fallback: 'N/A',
    sortField: 'created_at',
    width: '160px',
  },
  {
    id: 'channel',
    renderer: 'channel',
    header: 'Channel',
    tooltip: ' The specific store or marketplace integration where the order was captured.',
    width: '140px',
  },
  {id: 'orderStatus', renderer: 'orderStatus'},
  {id: 'allocationStatus', renderer: 'allocationStatus'},
  {id: 'fulfillmentStatus', renderer: 'fulfillmentStatus'},
  {id: 'syncStatusGroup', renderer: 'syncStatusGroup'},
  {id: 'channelOrderNoGroup', renderer: 'channelOrderNoGroup'},
  {id: 'action', renderer: 'action', width: '120px'},
]

export type OmsOrdersColumnConfig = {
  hiddenDynamicStatusChannelTypes: typeof hiddenDynamicStatusChannelTypes
  replacedDynamicStatusColumns: typeof replacedDynamicStatusColumns
  dynamicSyncStatusChannelTypes: typeof dynamicSyncStatusChannelTypes
  dynamicOrderNumberHiddenChannelTypes: typeof dynamicOrderNumberHiddenChannelTypes
  dynamicSyncStatusChannelTypeOrder: typeof dynamicSyncStatusChannelTypeOrder
  omsOrderLogSyncStatusChannelTypes: typeof omsOrderLogSyncStatusChannelTypes
  omsOrderLogSyncStatusChannelTypeOrder: typeof omsOrderLogSyncStatusChannelTypeOrder
  dynamicOrderNumberChannelTypeOrder: typeof dynamicOrderNumberChannelTypeOrder
  dynamicStatusColumnLabelOverrides: Partial<Record<string, string>>
  showCancelColumn: boolean
  orderStatusColumn: typeof orderStatusColumn
  columnLayout: OmsColumnDescriptor[]
}

export const omsOrdersColumnConfig: OmsOrdersColumnConfig = {
  hiddenDynamicStatusChannelTypes,
  replacedDynamicStatusColumns,
  dynamicSyncStatusChannelTypes,
  dynamicOrderNumberHiddenChannelTypes,
  dynamicSyncStatusChannelTypeOrder,
  omsOrderLogSyncStatusChannelTypes,
  omsOrderLogSyncStatusChannelTypeOrder,
  dynamicOrderNumberChannelTypeOrder,
  dynamicStatusColumnLabelOverrides,
  showCancelColumn,
  orderStatusColumn,
  columnLayout,
}

export default omsOrdersColumnConfig
